import {
  Avatar, Box, Button, Card, CardContent, Checkbox, Chip, Divider, FormControlLabel,
  Grid, IconButton, InputAdornment, MenuItem, Pagination, Popover, Stack,
  TextField, Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';
import VerifiedIcon from '@mui/icons-material/Verified';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';

import { PageHeader } from '@/components/PageHeader';
import { CenteredLoader } from '@/components/CenteredLoader';
import { useApiQuery } from '@/hooks/useApiQuery';
import { marketplace } from '@/api/endpoints';
import { formatDateTime } from '@/utils/format';
import type { MarketplaceSearchResult } from '@/types/medical';

/**
 * Find care.
 *
 * Search input + facets + ranked list. Each item shows clinician identity,
 * trust score, next available slot, and a "why this rank?" popover that
 * fetches the per-listing factor breakdown the backend produces from the
 * MarketplaceSearchPolicy weights (Pillar 12).
 *
 * Filter state lives in the URL — every search is a shareable link, and
 * it's easy to verify after a regulator complaint that two visitors
 * with the same query saw the same ranking.
 */

const PAGE_SIZE = 20;

const SORT_OPTIONS = [
  { code: 'relevance',     label: 'Most relevant' },
  { code: 'rating',        label: 'Highest rated' },
  { code: 'next_slot',     label: 'Soonest available' },
  { code: 'trust',         label: 'Highest trust score' },
];

const NEXT_SLOT_OPTIONS = [
  { code: '',           label: 'Anytime' },
  { code: 'today',      label: 'Today' },
  { code: 'this_week',  label: 'This week' },
  { code: 'this_month', label: 'This month' },
];

const MODALITY_OPTIONS = [
  { code: 'in_person', label: 'In person' },
  { code: 'video',     label: 'Video' },
  { code: 'phone',     label: 'Phone' },
  { code: 'async',     label: 'Async chat' },
  { code: 'home',      label: 'Home visit' },
];

export function FindCarePage() {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const sort = params.get('sort') ?? 'relevance';
  const nextSlot = params.get('next_slot') ?? '';
  const modalities = (params.get('modalities') ?? '').split(',').filter(Boolean);
  const specialties = (params.get('specialties') ?? '').split(',').filter(Boolean);
  const acceptsInsurance = params.get('accepts_insurance') === '1';
  const page = parseInt(params.get('page') ?? '1', 10);

  // Local state mirrors URL but lets us debounce the query keystrokes
  const [queryDraft, setQueryDraft] = useState(q);
  useEffect(() => { setQueryDraft(q); }, [q]);
  useEffect(() => {
    const handle = setTimeout(() => {
      if (queryDraft !== q) {
        const next = new URLSearchParams(params);
        if (queryDraft.trim()) next.set('q', queryDraft.trim()); else next.delete('q');
        next.set('page', '1');
        setParams(next, { replace: true });
      }
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryDraft]);

  const apiParams = useMemo(() => {
    const out: Record<string, string | number> = { page, page_size: PAGE_SIZE, sort };
    if (q) out.q = q;
    if (nextSlot) out.next_slot = nextSlot;
    if (modalities.length) out.modalities = modalities.join(',');
    if (specialties.length) out.specialties = specialties.join(',');
    if (acceptsInsurance) out.accepts_insurance = '1';
    return out;
  }, [q, sort, nextSlot, modalities.join(','), specialties.join(','), acceptsInsurance, page]);

  const searchQ = useApiQuery({
    queryKey: ['marketplace', 'search', apiParams],
    queryFn:  () => marketplace.search(apiParams) as Promise<MarketplaceSearchResult>,
  });

  const updateParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value == null || value === '') next.delete(key); else next.set(key, value);
    next.set('page', '1');
    setParams(next, { replace: true });
  };
  const toggleListParam = (key: string, value: string) => {
    const current = (params.get(key) ?? '').split(',').filter(Boolean);
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    updateParam(key, next.length ? next.join(',') : null);
  };

  return (
    <Stack spacing={3}>
      <PageHeader title="Find care" />

      <TextField
        fullWidth
        placeholder="Search by name, specialty, or condition…"
        value={queryDraft}
        onChange={(e) => setQueryDraft(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: 'text.secondary' }} />
            </InputAdornment>
          ),
        }}
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <FacetsPanel
            data={searchQ.data ?? null}
            sort={sort}
            nextSlot={nextSlot}
            modalities={modalities}
            specialties={specialties}
            acceptsInsurance={acceptsInsurance}
            onSortChange={(v) => updateParam('sort', v)}
            onNextSlotChange={(v) => updateParam('next_slot', v)}
            onModalityToggle={(v) => toggleListParam('modalities', v)}
            onSpecialtyToggle={(v) => toggleListParam('specialties', v)}
            onAcceptsInsuranceChange={(v) => updateParam('accepts_insurance', v ? '1' : null)}
          />
        </Grid>

        <Grid item xs={12} md={9}>
          {searchQ.isPending && <CenteredLoader label="Searching…" />}
          {searchQ.isError && (
            <Card><CardContent>
              <Typography variant="body2" color="error">
                Couldn't load results. {searchQ.error.message}
              </Typography>
            </CardContent></Card>
          )}
          {searchQ.data && (
            <SearchResults
              data={searchQ.data}
              page={page}
              onPageChange={(p) => updateParam('page', String(p))}
            />
          )}
        </Grid>
      </Grid>
    </Stack>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function FacetsPanel({
  data, sort, nextSlot, modalities, specialties, acceptsInsurance,
  onSortChange, onNextSlotChange, onModalityToggle, onSpecialtyToggle,
  onAcceptsInsuranceChange,
}: {
  data: MarketplaceSearchResult | null;
  sort: string;
  nextSlot: string;
  modalities: string[];
  specialties: string[];
  acceptsInsurance: boolean;
  onSortChange: (v: string) => void;
  onNextSlotChange: (v: string) => void;
  onModalityToggle: (v: string) => void;
  onSpecialtyToggle: (v: string) => void;
  onAcceptsInsuranceChange: (v: boolean) => void;
}) {
  return (
    <Stack spacing={2}>
      <Card>
        <CardContent>
          <TextField
            fullWidth select size="small" label="Sort by"
            value={sort}
            onChange={(e) => onSortChange(e.target.value)}
          >
            {SORT_OPTIONS.map((o) => (
              <MenuItem key={o.code} value={o.code}>{o.label}</MenuItem>
            ))}
          </TextField>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="overline" color="text.secondary"
                      sx={{ display: 'block', mb: 1.5 }}>
            Availability
          </Typography>
          <TextField
            fullWidth select size="small" label="Next available"
            value={nextSlot}
            onChange={(e) => onNextSlotChange(e.target.value)}
          >
            {NEXT_SLOT_OPTIONS.map((o) => {
              const facetKey = o.code as 'today' | 'this_week' | 'this_month';
              const count = o.code && data?.facets?.next_slot
                ? data.facets.next_slot[facetKey] : null;
              return (
                <MenuItem key={o.code} value={o.code}>
                  {o.label}{count != null && ` (${count})`}
                </MenuItem>
              );
            })}
          </TextField>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="overline" color="text.secondary"
                      sx={{ display: 'block', mb: 1.5 }}>
            Modality
          </Typography>
          <Stack spacing={0.5}>
            {MODALITY_OPTIONS.map((m) => {
              const count = data?.facets?.modality?.find((f) => f.code === m.code)?.count;
              return (
                <FormControlLabel
                  key={m.code}
                  control={<Checkbox size="small"
                                     checked={modalities.includes(m.code)}
                                     onChange={() => onModalityToggle(m.code)} />}
                  label={
                    <Typography variant="body2">
                      {m.label}{count != null && (
                        <Box component="span" sx={{ color: 'text.secondary', ml: 0.5 }}
                             className="tabular">({count})</Box>
                      )}
                    </Typography>
                  }
                />
              );
            })}
          </Stack>
        </CardContent>
      </Card>

      {data?.facets?.specialty && data.facets.specialty.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="overline" color="text.secondary"
                        sx={{ display: 'block', mb: 1.5 }}>
              Specialty
            </Typography>
            <Stack spacing={0.5}>
              {data.facets.specialty.slice(0, 8).map((f) => (
                <FormControlLabel
                  key={f.id}
                  control={<Checkbox size="small"
                                     checked={specialties.includes(String(f.id))}
                                     onChange={() => onSpecialtyToggle(String(f.id))} />}
                  label={
                    <Typography variant="body2">
                      {f.name}
                      <Box component="span" sx={{ color: 'text.secondary', ml: 0.5 }}
                           className="tabular">({f.count})</Box>
                    </Typography>
                  }
                />
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent>
          <Typography variant="overline" color="text.secondary"
                      sx={{ display: 'block', mb: 1.5 }}>
            Insurance
          </Typography>
          <FormControlLabel
            control={<Checkbox size="small"
                               checked={acceptsInsurance}
                               onChange={(_, v) => onAcceptsInsuranceChange(v)} />}
            label={
              <Typography variant="body2">
                Accepts insurance
                {data?.facets?.accepts_insurance && (
                  <Box component="span" sx={{ color: 'text.secondary', ml: 0.5 }}
                       className="tabular">
                    ({data.facets.accepts_insurance.yes})
                  </Box>
                )}
              </Typography>
            }
          />
        </CardContent>
      </Card>
    </Stack>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function SearchResults({
  data, page, onPageChange,
}: {
  data: MarketplaceSearchResult;
  page: number;
  onPageChange: (p: number) => void;
}) {
  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" color="text.secondary" className="tabular">
          {data.total} clinician{data.total === 1 ? '' : 's'} found
        </Typography>
        {data.policy_id != null && (
          <Typography variant="caption" color="text.secondary"
                      className="tabular">
            policy #{data.policy_id} · search {data.search_hash.slice(0, 8)}
          </Typography>
        )}
      </Stack>

      {data.items.length === 0 ? (
        <Card><CardContent>
          <Typography variant="body2" color="text.secondary">
            No clinicians match this search. Try removing some filters.
          </Typography>
        </CardContent></Card>
      ) : (
        <Stack spacing={1.5}>
          {data.items.map((item) => (
            <ResultCard key={item.clinician_id} item={item} />
          ))}
        </Stack>
      )}

      {totalPages > 1 && (
        <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
          <Pagination count={totalPages} page={page}
                      onChange={(_, p) => onPageChange(p)} />
        </Stack>
      )}
    </Stack>
  );
}

function ResultCard({ item }: { item: MarketplaceSearchResult['items'][0] }) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const initials = (item.display_name || '?').split(' ')
    .map((p) => p[0] ?? '').join('').slice(0, 2);

  return (
    <Card>
      <CardContent>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
          <Avatar src={item.photo_url || undefined}
                  sx={{ width: 64, height: 64, bgcolor: 'primary.main', fontSize: '1.4rem' }}>
            {initials}
          </Avatar>

          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="h6" component="h3"
                          sx={{ '& a': { color: 'inherit', textDecoration: 'none' },
                                '& a:hover': { color: 'primary.main' } }}>
                <RouterLink to={`/clinicians/${item.practice_slug}`}>
                  {item.display_name}
                </RouterLink>
              </Typography>
              {item.trust_score != null && item.trust_score >= 80 && (
                <VerifiedIcon color="primary" sx={{ fontSize: 18 }} />
              )}
            </Stack>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {item.specialties.join(' · ')}
            </Typography>

            <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap' }}>
              {item.rating_count > 0 && (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <StarIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                  <Typography variant="body2" className="tabular">
                    {parseFloat(item.rating_avg).toFixed(1)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary"
                              className="tabular">
                    ({item.rating_count})
                  </Typography>
                </Stack>
              )}
              {item.languages.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {item.languages.join(', ').toUpperCase()}
                </Typography>
              )}
              {item.next_slot_at && (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <EventAvailableIcon color="success" sx={{ fontSize: 16 }} />
                  <Typography variant="caption" color="success.main"
                              className="tabular">
                    {formatDateTime(item.next_slot_at)}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Box>

          <Stack alignItems="flex-end" spacing={1}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="caption" color="text.secondary">
                rank score
              </Typography>
              <Typography variant="subtitle1" className="tabular">
                {parseFloat(item.score).toFixed(2)}
              </Typography>
              <IconButton size="small"
                          onClick={(e) => setAnchorEl(e.currentTarget)}>
                <HelpOutlineIcon fontSize="small" />
              </IconButton>
            </Stack>
            <Button size="small" variant="contained" component={RouterLink}
                    to={`/clinicians/${item.practice_slug}`}>
              View profile
            </Button>
          </Stack>
        </Stack>
      </CardContent>

      <RankExplanationPopover
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        clinicianSlug={item.practice_slug}
      />
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

type RankExplanation = {
  total: number;
  factors: Array<{
    code: string;
    name: string;
    weight: number;
    raw_value: number;
    contribution: number;
    description: string;
  }>;
  policy_id: number;
  policy_version: string;
};

function RankExplanationPopover({
  anchorEl, onClose, clinicianSlug,
}: {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  clinicianSlug: string;
}) {
  const open = Boolean(anchorEl);
  const q = useApiQuery<RankExplanation>({
    queryKey: ['marketplace', 'rank-explanation', clinicianSlug],
    queryFn:  () => marketplace.clinicians.rankExplanation(clinicianSlug)
                      .then((d) => d as RankExplanation),
    enabled:  open,
  });

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{ paper: { sx: { p: 2, maxWidth: 380 } } }}
    >
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Why this rank?</Typography>
      <Typography variant="caption" color="text.secondary"
                  sx={{ display: 'block', mb: 1.5 }}>
        Per-factor breakdown from the marketplace policy currently in effect.
      </Typography>

      {q.isPending && <Typography variant="body2">Loading…</Typography>}
      {q.isError && (
        <Typography variant="body2" color="error">
          Couldn't load explanation. {q.error.message}
        </Typography>
      )}
      {q.data && (
        <>
          <Stack spacing={1} divider={<Divider flexItem />} sx={{ mb: 1.5 }}>
            {q.data.factors.map((f) => (
              <Box key={f.code}>
                <Stack direction="row" justifyContent="space-between"
                       alignItems="baseline">
                  <Typography variant="body2" fontWeight={500}>{f.name}</Typography>
                  <Typography variant="body2" className="tabular">
                    +{f.contribution.toFixed(2)}
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  weight <span className="tabular">{f.weight.toFixed(2)}</span>
                  {' · raw '}<span className="tabular">{f.raw_value.toFixed(2)}</span>
                  {' — '}{f.description}
                </Typography>
              </Box>
            ))}
          </Stack>
          <Divider sx={{ mb: 1 }} />
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" fontWeight={600}>Total</Typography>
            <Typography variant="body2" fontWeight={600} className="tabular">
              {q.data.total.toFixed(2)}
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary"
                      sx={{ display: 'block', mt: 1 }}>
            Policy <span className="tabular">#{q.data.policy_id}</span>
            {' · '}<RouterLink to="/transparency/why-this-rank"
                               style={{ color: 'inherit' }}>
              read full methodology
            </RouterLink>
          </Typography>
        </>
      )}

      <Box sx={{ textAlign: 'right', mt: 1.5 }}>
        <Chip size="small" label="Close" onClick={onClose} />
      </Box>
    </Popover>
  );
}
